import { styled, css, useTheme } from '@vebox/etui-system';
import { createElement, Fragment } from 'react';
import { ellipsis } from './utilities/ellipsis.js';
import useResponsive from './useResponsive/useResponsive.js';
import { _ as _objectWithoutProperties, a as _objectSpread2, b as _extends } from './_rollupPluginBabelHelpers-3b7bd988.js';
import { Div, Button, Ul, Span } from '@vebox/etui-system-elements';
import Icon from './Icon/Icon.js';
import { icons } from './Icon/icons.js';
import { darken } from '@vebox/etui-colors';
import Downshift$1 from 'downshift';
import Spinner from './Spinner/Spinner.js';
import { S as SpinnerSize } from './utils-fabc7ae7.js';
import { D as DropdownItemText, C as ContextMenuItem } from './ContextMenuItem-4b6a511e.js';

const _excluded = ["isLeftRightNavEnabled", "theme"];
const DropdownRootDiv = styled(Div).attrs(_ref => {
  let {
      isLeftRightNavEnabled,
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  return _objectSpread2({
    display: 'block',
    position: 'relative',
    height: theme.shape.headerTopHeight,
    m: `${isLeftRightNavEnabled ? `0 ${theme.spacing.xlg}px` : '0'}`
  }, props);
})``;

const _excluded$1 = ["hasPlaceholderText", "isOpen", "isSelected", "isLeftRightNavEnabled", "theme"];
const TriggerButton = styled(Button).attrs(_ref => {
  let {
      hasPlaceholderText,
      isOpen,
      isSelected,
      isLeftRightNavEnabled,
      theme
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded$1);
  return _objectSpread2({
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSizeBase,
    letterSpacing: theme.typography.button.letterSpacing,
    width: 'auto',
    color: isOpen ? theme.palette.common.white : isSelected ? theme.palette.common.white : theme.palette.black[40],
    bg: isOpen ? darken(theme.palette.black[80], 0.2) : isSelected ? darken(theme.palette.black[80], 0.2) : theme.palette.black[80],
    p: `0 ${isLeftRightNavEnabled ? '42px' : `${theme.spacing.md}px`}`,
    border: 0,
    cursor: 'pointer'
  }, props);
})`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-shrink: 0;
  outline: none;
  position: relative;
  height: 100%;
  user-select: none;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({
  theme
}) => darken(theme.palette.black[80], 0.2)};
  }
`;

const DropdownArrowDiv = styled(Div)`
  display: flex;
  align-items: center;
  bottom: 0;
  position: absolute;
  top: 0;
  right: ${({
  theme
}) => theme.spacing.lg}px;
  color: ${({
  theme
}) => theme.palette.black[40]};
`;

const DropdownMenuUl = styled(Ul).attrs(props => _objectSpread2({
  minWidth: '220px',
  width: '100%',
  maxHeight: '250px',
  m: 0,
  p: 0
}, props))`
  position: absolute;
  top: 0;
  left: ${({
  alignLeft
}) => alignLeft ? 0 : 'auto'};
  right: ${({
  alignRight
}) => alignRight ? 0 : 'auto'};
  background-color: ${({
  theme
}) => darken(theme.palette.black[80], 0.2)};
  border-top: 1px solid ${({
  theme
}) => theme.palette.black['60']};
  overflow-y: auto;

  ${({
  hasPrimaryAction
}) => {
  if (!hasPrimaryAction) {
    return css`
        box-shadow: 0 5px 30px -2px rgba(0, 0, 0, 0.5);
        border: 1px solid ${({
      theme
    }) => darken(theme.palette.black[80], 0.3)};
      `;
  }
  return ``;
}};
`;

const PlaceholderIconSpan = styled(Div)`
  display: flex;
  color: inherit;
  margin-right: ${({
  hasPlaceholderText,
  theme
}) => hasPlaceholderText ? `${theme.spacing.xs}px` : 0};
  vertical-align: 2px;
`;

const ArrowSpan = styled(Span)`
  display: flex;
  position: absolute;
  height: 100%;
  align-items: center;
  top: 0;
  cursor: pointer;
  z-index: 1;
  width: 42px;
  justify-content: center;
  color: ${({
  theme
}) => theme.palette.black[40]};
  right: ${({
  isRight
}) => isRight ? '0' : 'auto'};

  &:hover,
  &:active {
    color: ${({
  theme
}) => theme.palette.black[20]};
  }
`;

const PrimaryItem = styled(Div)`
  border-bottom: 1px solid ${({
  theme
}) => theme.palette.black['60']};
`;

const ListHeaderWrapper = styled(Div).attrs(({
  theme
}) => ({
  p: `${theme.spacing.md}px ${theme.spacing.sm}px ${theme.spacing.xxs}px ${theme.spacing.lg}px`,
  color: theme.palette.black['40'],
  fontSize: theme.typography.fontSizeSmall
}))`
  display: flex;
  align-items: center;
`;

const PrimaryItemWrapper = styled(Div)`
  &:hover {
    background-color: ${({
  theme
}) => darken(theme.palette.black[80], 0.3)};
  }
`;

let ContextMenuItemType = /*#__PURE__*/function (ContextMenuItemType) {
  ContextMenuItemType[ContextMenuItemType["STATIC_TOP"] = 1] = "STATIC_TOP";
  ContextMenuItemType[ContextMenuItemType["STATIC_BOTTOM"] = 2] = "STATIC_BOTTOM";
  return ContextMenuItemType;
}({});

const Downshift = Downshift$1;
const renderDropdownMarkup = (itemKey, items, placeholder, placeholderIcon, disableArrowIcon, alignLeft, alignRight, styles, fitToSmallScreen, enableLeftRightNav, loading, loadingText = 'Loading...', listHeaderLabel, listHeaderIcon, itemsListTestId, contextMenuValueTestId, useTextRtlDirection, isSmView, theme, onOpen) => function Dropdown(props) {
  const showOnSmallScreen = fitToSmallScreen && isSmView;
  const isPrimaryActionDefined = items.some(item => item.type === ContextMenuItemType.STATIC_TOP);
  const isListHeaderLabelDefined = !!listHeaderLabel;
  const hasPlaceholderText = () => {
    if (showOnSmallScreen) return false;
    if (!showOnSmallScreen && placeholder !== undefined) return true;
    return false;
  };
  const getSelectedItem = selectedItemKey => items.find(item => item[itemKey] === selectedItemKey);
  const changeItem = (_, moveForward) => {
    event.preventDefault();
    items.forEach((item, index) => {
      const selectedItem = getSelectedItem(props.selectedItem);
      const selectedItemIndex = items.indexOf(selectedItem);
      if (!props.selectedItem) {
        const nextIndex = moveForward ? 0 : items.length - 1;
        props.selectItem(items[nextIndex]);
      }
      if (props.selectedItem === item[itemKey]) {
        moveForward ? props.selectItem(items[index + 1]) : props.selectItem(items[index - 1]);
      }
      if (props.selectedItem !== item[itemKey]) {
        if (selectedItemIndex === items.length - 1 && moveForward) {
          props.selectItem(items[0]);
        }
        if (selectedItemIndex === 0 && !moveForward) {
          props.selectItem(items[items.length - 1]);
        }
      }
    });
  };
  const changeLeft = event => changeItem(event, false);
  const changeRight = event => changeItem(event, true);
  const renderItemsByType = _ => items.map((item, index) => {
    const itemContent = /*#__PURE__*/createElement(ContextMenuItem, _extends({
      addSpaceLeft: isListHeaderLabelDefined,
      useTextRtlDirection: useTextRtlDirection,
      isActive: props.highlightedIndex === index,
      isSelected: placeholder && props.selectedItem === item[itemKey],
      isAction: item.onClick !== undefined,
      onClick: item.onClick
    }, item, props.getItemProps({
      index,
      item,
      isSelected: placeholder && props.selectedItem === item[itemKey],
      key: item[itemKey]
    })), item.name);
    return item.type === ContextMenuItemType.STATIC_TOP ? /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(PrimaryItemWrapper, null, /*#__PURE__*/createElement(PrimaryItem, null, itemContent)), isListHeaderLabelDefined && /*#__PURE__*/createElement(ListHeaderWrapper, null, listHeaderIcon && /*#__PURE__*/createElement(Icon, _extends({}, listHeaderIcon, {
      size: 16
    })), /*#__PURE__*/createElement("span", null, listHeaderLabel))) : itemContent;
  });
  const handleClick = () => {
    props.toggleMenu();
    if (!props.isOpen && onOpen) {
      onOpen();
    }
  };
  return /*#__PURE__*/createElement(DropdownRootDiv, _extends({}, props.getRootProps(), styles.root), !loading && enableLeftRightNav && /*#__PURE__*/createElement(ArrowSpan, {
    onClick: changeLeft
  }, /*#__PURE__*/createElement(Icon, {
    glyph: icons.CHEVRON_DOWN,
    rotate: 90
  })), /*#__PURE__*/createElement(TriggerButton, _extends({
    isOpen: props.isOpen,
    isSelected: props.selectedItem,
    textCenter: disableArrowIcon,
    hasPlaceholderText: hasPlaceholderText(),
    isLeftRightNavEnabled: enableLeftRightNav,
    useTextRtlDirection: useTextRtlDirection
  }, styles.triggerContainer, props.getToggleButtonProps({}), {
    onClick: handleClick
  }), placeholderIcon && /*#__PURE__*/createElement(PlaceholderIconSpan, {
    hasPlaceholderText: hasPlaceholderText()
  }, /*#__PURE__*/createElement(Icon, placeholderIcon)), /*#__PURE__*/createElement(Span, ellipsis(), !showOnSmallScreen && placeholder && props.selectedItem && /*#__PURE__*/createElement(DropdownItemText, {
    testId: contextMenuValueTestId
  }, getSelectedItem(props.selectedItem).name), !showOnSmallScreen && placeholder && !props.selectedItem && /*#__PURE__*/createElement(Span, {
    whiteSpace: "nowrap",
    fontWeight: theme.typography.fontWeightBold
    // @ts-ignore
    ,
    direction: useTextRtlDirection ? 'rtl' : 'ltr',
    width: useTextRtlDirection ? '75%' : '90%',
    textAlign: disableArrowIcon ? 'center' : 'unset'
  }, placeholder)), loading && /*#__PURE__*/createElement(DropdownArrowDiv, null, /*#__PURE__*/createElement(Spinner, {
    size: SpinnerSize.xSmall,
    isTransparent: true
  })), !loading && !showOnSmallScreen && !disableArrowIcon && !enableLeftRightNav && /*#__PURE__*/createElement(DropdownArrowDiv, null, /*#__PURE__*/createElement(Icon, {
    glyph: icons.DOUBLE_ARROW
  }))), !loading && enableLeftRightNav && /*#__PURE__*/createElement(ArrowSpan, {
    isRight: true,
    onClick: changeRight
  }, /*#__PURE__*/createElement(Icon, {
    glyph: icons.CHEVRON_DOWN,
    rotate: -90
  })), /*#__PURE__*/createElement(Div, {
    position: "relative",
    zIndex: 2
  }, props.isOpen && /*#__PURE__*/createElement(DropdownMenuUl, _extends({
    alignLeft: alignLeft,
    alignRight: alignRight,
    hasPrimaryAction: isPrimaryActionDefined
  }, styles.menuContainer, props.getMenuProps({
    refKey: 'elRef'
  }), {
    testId: itemsListTestId
  }), loading && /*#__PURE__*/createElement(ContextMenuItem, {
    key: "loader",
    name: "loading",
    isActive: false,
    isSelected: false,
    isAction: false
  }, loadingText), !loading && isListHeaderLabelDefined && !isPrimaryActionDefined && /*#__PURE__*/createElement(ListHeaderWrapper, null, listHeaderIcon && /*#__PURE__*/createElement(Icon, _extends({}, listHeaderIcon, {
    size: 16
  })), /*#__PURE__*/createElement("span", null, listHeaderLabel)), !loading && renderItemsByType())));
};
const ContextMenu = ({
  placeholder,
  placeholderIcon,
  itemKey,
  items,
  itemToString,
  selectedItem,
  onChange,
  onUserAction,
  disableArrowIcon,
  position = 'left',
  styles = {},
  fitToSmallScreen,
  enableLeftRightNav,
  loading,
  loadingText,
  listHeaderLabel,
  listHeaderIcon,
  useTextRtlDirection,
  itemsListTestId,
  contextMenuValueTestId,
  onOpen
}) => {
  const theme = useTheme();
  const {
    screenIsAtMost
  } = useResponsive({
    xs: parseInt(theme.breakpoints[0], 10),
    sm: parseInt(theme.breakpoints[1], 10),
    md: parseInt(theme.breakpoints[2], 10),
    lg: parseInt(theme.breakpoints[3], 10)
  });
  const isSmView = screenIsAtMost('xs');
  const allItems = [...items];
  const alignLeft = position === 'left';
  const alignRight = position === 'right';
  const handleOnChange = item => {
    if (onChange) {
      onChange(item);
    }
    if (item.onClick) {
      item.onClick(item);
    }
  };
  return /*#__PURE__*/createElement(Downshift, {
    itemToString: itemToString,
    selectedItem: selectedItem,
    onChange: handleOnChange,
    onUserAction: onUserAction
  }, renderDropdownMarkup(itemKey, allItems, placeholder, placeholderIcon, disableArrowIcon, alignLeft, alignRight, styles, fitToSmallScreen, enableLeftRightNav, loading, loadingText, listHeaderLabel, listHeaderIcon, itemsListTestId, contextMenuValueTestId, useTextRtlDirection, isSmView, theme, onOpen));
};

export { ContextMenu as C, ContextMenuItemType as a };
